import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography, MenuItem } from '@mui/material';
import { InputField } from '../../../components/InputController';
import { useFormik } from 'formik';
import {
  ApplicationQuestionAndAnswerDto,
  CustomQuestionTypeEnum,
  CustomQuestionResponseTypeEnum,
  ApplicationVehicleDtoTypeEnum,
  UpsertApplicantVehicleV2Dto,
  ApplicationQuestionResponseV2Dto,
} from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import CustomDropdown from '../../../components/customDropdown';
import { applicationStyles } from './CustomStyles';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { getApplicantVehicleRecord, selectApplicantsState, updateApplicantVehicleRecord, updateCustomQuestionsAnswersV2 } from '../sagas/applicants/applicantsSlice';
import * as Yup from 'yup';
import CustomQuestionsSection from './CustomQuestionsSection';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';

interface PropsType {}

const schemaValidationApplicationVehicle = Yup.object({
  Type: Yup.string().max(45, 'Max length is 45 characters').required('Please select a type'),
  Make: Yup.string().max(45, 'Max length is 45 characters').required('Please enter a make'),
  Model: Yup.string().max(45, 'Max length is 45 characters').required('Please enter a model'),
  Year: Yup.string().max(45, 'Max length is 45 characters').required('Please enter a year'),
  Color: Yup.string().max(45, 'Max length is 45 characters').required('Please enter a color'),
  LicensePlateNo: Yup.string().required("Please enter your vehicle's license plate number"),
  Length: Yup.string().required('Please enter the length of your vehicle'),
});

type schemaValidationApplicationVehicleType = Yup.InferType<typeof schemaValidationApplicationVehicle>;

export default function ApplicationVehicle(props: PropsType) {
  const query = useQuery();
  const hasValidationQueryParam = !!query.get('validation');
  const classes = applicationStyles();
  const { id: vehicleUuidParam } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  //selectors
  const { ipaUser } = useSelector(selectAuthState);
  const {
    isFetchingApplicantVehicleRecordSuccess,
    isFetchingApplicantVehicleRecordFailed,
    applicantVehicleRecord,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    Application,
    CustomQuestionAnswers,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
  } = useSelector(selectApplicantsState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);

  //local states
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [currentVehicleUuid] = useState(vehicleUuidParam);
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<Array<ApplicationQuestionAndAnswerDto>>([]);
  const [initialFormValues, setInitialFormValues] = useState<schemaValidationApplicationVehicleType>({
    Type: '',
    Make: '',
    Model: '',
    Year: '',
    Color: '',
    LicensePlateNo: '',
    Length: '',
  });

  useEffect(() => {
    if (ipaUser?.currentProfile.uuid && isFetchingApplicantVehicleRecordSuccess) {
      setInitialFormValues({
        ...initialFormValues,
        Type: applicantVehicleRecord.Type ?? '',
        Make: applicantVehicleRecord.Make ?? '',
        Model: applicantVehicleRecord.Model ?? '',
        Year: applicantVehicleRecord.Year ?? '',
        Color: applicantVehicleRecord.Color ?? '',
        LicensePlateNo: applicantVehicleRecord.LicensePlateNo ?? '',
        Length: applicantVehicleRecord.Length ?? '',
      });
    }
  }, [ipaUser, currentVehicleUuid, isFetchingApplicantVehicleRecordSuccess]);

  const formik = useFormik<schemaValidationApplicationVehicleType>({
    initialValues: initialFormValues,
    validationSchema: schemaValidationApplicationVehicle,
    onSubmit: async (values) => {},
    enableReinitialize: true,
    validateOnMount: true,
  });

  //populate custom questions once prop group details received
  useEffect(() => {
    if (propertyGroupDetails?.Uuid && isFetchingCustomQuestionAnswersSuccess) {
      const organizationPetsCustomQuestions = (propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsQuestions ?? []).filter(
        (q) => q.CustomQuestionType === CustomQuestionTypeEnum.VehicleV1,
      );
      setQuestionsAndAnswers(
        organizationPetsCustomQuestions.map((q) => {
          const previouslySavedResponseToThisQuestion = CustomQuestionAnswers?.find((a) => a.QuestionUuid === q.Uuid && a.BelongsToUuid === currentVehicleUuid);

          const question: ApplicationQuestionAndAnswerDto = {
            ...q,
            ApplicantUuid: previouslySavedResponseToThisQuestion?.ApplicantUuid!,
            BelongsToEntityUuid: currentVehicleUuid,
          };

          switch (question.CustomQuestionResponseType) {
            case CustomQuestionResponseTypeEnum.FreeText:
              question.TextQuestionAnswer = previouslySavedResponseToThisQuestion?.Response?.toString();
              break;
            case CustomQuestionResponseTypeEnum.Boolean:
              question.BooleanQuestionAnswer = previouslySavedResponseToThisQuestion?.Response === undefined ? undefined : Boolean(previouslySavedResponseToThisQuestion?.Response);
              break;
            default:
              break;
          }

          return question;
        }),
      );
    }
  }, [propertyGroupDetails, isFetchingCustomQuestionAnswersSuccess]);

  const handleSaveCustomQuestionAnswer = (e: any, questionUuid: string) => {
    const answeredQuestion = questionsAndAnswers.find((q) => q.Uuid === questionUuid);

    if (answeredQuestion) {
      dispatch({
        type: updateCustomQuestionsAnswersV2.type,
        payload: {
          applicationUuid: Application.Uuid,
          upsertDto: {
            QuestionUuid: answeredQuestion?.Uuid,
            ResponseType: answeredQuestion?.CustomQuestionResponseType,
            Response: answeredQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean ? e.target.value === 'true' : e.target.value,
            BelongsToUuid: currentVehicleUuid,
            ApplicantUuid: ipaUser?.currentProfile.uuid,
          } as ApplicationQuestionResponseV2Dto,
        },
      });
    }
  };

  const handleCustomQuestionFieldChanged = (e: any, questionUuid: string) => {
    setQuestionsAndAnswers(
      questionsAndAnswers.map((qa) => {
        if (qa.Uuid === questionUuid) {
          //found our question.

          if (qa.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
            return {
              ...qa,
              BooleanQuestionAnswer: e.target.value === 'true',
            };
          } else
            return {
              ...qa,
              TextQuestionAnswer: e.target.value,
            };
        }
        return qa;
      }),
    );
  };

  const handleFormValuesChanged = (e: any) => {
    /*
     */

    const payload = {
      Type: !formik.errors.Type ? formik.values.Type : undefined,
      Make: !formik.errors.Type ? formik.values.Make : undefined,
      Model: !formik.errors.Type ? formik.values.Model : undefined,
      Year: !formik.errors.Type ? formik.values.Year : undefined,
      Color: !formik.errors.Type ? formik.values.Color : undefined,
      LicensePlateNo: !formik.errors.Type ? formik.values.LicensePlateNo || undefined : undefined,
      Length: !formik.errors.Type ? formik.values.Length || undefined : undefined,
    } as UpsertApplicantVehicleV2Dto;

    for (const [key] of Object.entries(payload)) {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    }

    dispatch({
      type: updateApplicantVehicleRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        vehicleUuid: currentVehicleUuid,
        updateDto: payload,
      },
    });
  };

  //fetch the vehicle record on load
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid && currentVehicleUuid) {
      dispatch({
        type: getApplicantVehicleRecord.type,
        payload: {
          applicantUuid: ipaUser.currentProfile.uuid,
          vehicleUuid: currentVehicleUuid,
        },
      });
    }
  }, [dispatch, ipaUser, currentVehicleUuid]);

  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess && isFetchingApplicantVehicleRecordSuccess && isFetchingApplicationSuccess && isFetchingCustomQuestionAnswersSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingPropertyGroupDetailsFailed || isFetchingApplicantVehicleRecordFailed || isFetchingApplicationFailure || isFetchingCustomQuestionAnswersFailure) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the vehicle information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [
    isFetchingPropertyGroupDetailsSuccess,
    isFetchingPropertyGroupDetailsFailed,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    isFetchingApplicantVehicleRecordSuccess,
    isFetchingApplicantVehicleRecordFailed,
  ]);

  return (
    <Box sx={{ width: '100%', minHeight: '60vh' }}>
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box sx={{ width: '100%', p: 4, minHeight: 'inherit' }}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              {localError && <Typography className={classes.globalText}>{localError}</Typography>}
            </Grid>

            <Grid item xs={12} sm={6} lg={4.5} xl={3.5}>
              <Typography sx={{ mb: 2 }} className={classes.labelText}>
                Vehicle Details
              </Typography>
              <CustomDropdown
                isFullWidth
                label={'Car / Truck'}
                value={formik.values.Type ?? ''}
                handleChange={(e) => {
                  formik.setFieldValue('Type', e.target.value);
                }}
                menuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                    },
                  },
                }}
                error={formik.errors.Type || (hasValidationQueryParam && !formik.values.Type ? 'Please select an option' : undefined)}
                showAsterisk={!formik.values.Type}
                handleBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
              >
                <MenuItem style={{ height: '36px' }} value="">
                  Select one
                </MenuItem>
                {Object.values(ApplicationVehicleDtoTypeEnum).map((value, index) => (
                  <MenuItem style={{ height: '36px' }} value={value} key={index}>
                    {value}
                  </MenuItem>
                ))}
              </CustomDropdown>
              <InputField
                label={'Make'}
                isError={!!formik.errors?.Make && hasValidationQueryParam}
                errors={formik.errors?.Make}
                touched={formik.touched?.Make}
                {...formik.getFieldProps('Make')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                showAsterisk={!formik.values.Make}
              />
              <InputField
                label={'Model'}
                isError={!!formik.errors?.Model && hasValidationQueryParam}
                errors={formik.errors?.Model}
                touched={formik.touched?.Model}
                {...formik.getFieldProps('Model')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                showAsterisk={!formik.values.Model}
              />
              <InputField
                label={'Year'}
                isError={!!formik.errors?.Year && hasValidationQueryParam}
                errors={formik.errors?.Year}
                touched={formik.touched?.Year}
                {...formik.getFieldProps('Year')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                showAsterisk={!formik.values.Year}
              />
              <InputField
                label={'Color'}
                isError={!!formik.errors?.Color && hasValidationQueryParam}
                errors={formik.errors?.Color}
                touched={formik.touched?.Color}
                {...formik.getFieldProps('Color')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                showAsterisk={!formik.values.Color}
              />
              {(formik.values.Type === ApplicationVehicleDtoTypeEnum.Car ||
                formik.values.Type === ApplicationVehicleDtoTypeEnum.Truck ||
                formik.values.Type === ApplicationVehicleDtoTypeEnum.Suv ||
                formik.values.Type === ApplicationVehicleDtoTypeEnum.Rv ||
                formik.values.Type === ApplicationVehicleDtoTypeEnum.Trailer) && (
                <InputField
                  name={'LicensePlateNo'}
                  label={'License Plate'}
                  value={formik.values.LicensePlateNo ?? ''}
                  isError={
                    !formik.values.LicensePlateNo &&
                    [
                      ApplicationVehicleDtoTypeEnum.Car,
                      ApplicationVehicleDtoTypeEnum.Truck,
                      ApplicationVehicleDtoTypeEnum.Suv,
                      ApplicationVehicleDtoTypeEnum.Rv,
                      ApplicationVehicleDtoTypeEnum.Trailer,
                    ].includes(formik.values.Type) &&
                    hasValidationQueryParam
                  }
                  errors={formik.errors?.LicensePlateNo}
                  touched={formik.touched?.LicensePlateNo}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                  onChange={(e) => formik.setFieldValue('LicensePlateNo', e.target.value)}
                  showAsterisk={!formik.values.LicensePlateNo}
                />
              )}
              {formik.values.Type &&
                !(
                  formik.values.Type === ApplicationVehicleDtoTypeEnum.Car ||
                  formik.values.Type === ApplicationVehicleDtoTypeEnum.Truck ||
                  formik.values.Type === ApplicationVehicleDtoTypeEnum.Suv
                ) && (
                  <InputField
                    name={'Length'}
                    label={'Length'}
                    value={formik.values.Length ?? ''}
                    isError={
                      !formik.values.Length &&
                      ![
                        ApplicationVehicleDtoTypeEnum.Car,
                        ApplicationVehicleDtoTypeEnum.Truck,
                        ApplicationVehicleDtoTypeEnum.Suv,
                        ApplicationVehicleDtoTypeEnum.Rv,
                        ApplicationVehicleDtoTypeEnum.Trailer,
                      ].some((s) => s === formik.values.Type) &&
                      hasValidationQueryParam
                    }
                    errors={formik.errors?.Length}
                    touched={formik.touched?.Length}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      handleFormValuesChanged(e);
                    }}
                    onChange={(e) => formik.setFieldValue('Length', e.target.value)}
                    showAsterisk={!formik.values.Length}
                  />
                )}
            </Grid>
            <CustomQuestionsSection
              questionsAndAnswers={questionsAndAnswers}
              hasValidationQueryParam={hasValidationQueryParam}
              onFieldChange={handleCustomQuestionFieldChanged}
              onFieldUpdate={handleSaveCustomQuestionAnswer}
            />
          </Grid>
        </Box>
      )}
    </Box>
  );
}
